/**
 * FAQ (V2) — ONE component per kind, composed from the Production Foundation (Phase
 * 11.5.1) and ported from the reference "faq" section. Reads `section.layout` and applies
 * the matching CSS layout (faq.css); the React tree mirrors the reference (centered Info →
 * ul of accordion items). The reference has a single FAQ layout (accordion); the frozen
 * `cards`/`minimal` have no reference design and fall back to this accordion.
 *
 * Accordion: per-item local React state; instant CSS display swap for the body (as in the
 * reference); the chevron rotates. Header is an accessible <button> (aria-expanded/controls)
 * — an a11y improvement over the reference's clickable <div>, visually identical.
 *
 * Public API unchanged (SectionComponentProps<'faq'>) — Registry/Renderer/Engine untouched.
 * Reads only the shared content grammar: eyebrow, heading, subheading, items[], actions.
 * Each item → one accordion row (title = question, description = answer).
 */
import { useState } from 'react'
import type { SectionComponentProps } from '../../runtime/types'
import type { ContentItem } from '../../schema'
import { Section, Container, InfoBlock, Actions, Icon, Text, cx } from '../shared'
import './faq.css'

function FaqItem({ item, index, sectionId }: { item: ContentItem; index: number; sectionId: string }) {
  const [open, setOpen] = useState(false)
  const bodyId = `${sectionId}-faq-${index}`
  const btnId = `${bodyId}-btn`
  return (
    <>
      <button
        type="button"
        id={btnId}
        className="sbp-faq__q"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="sbp-faq__q-text">{item.title}</span>
        <Icon name="chevron-down" className={cx('sbp-faq__icon', open && 'sbp-faq__icon--open')} />
      </button>
      <div
        id={bodyId}
        role="region"
        aria-labelledby={btnId}
        className={cx('sbp-faq__a', open && 'sbp-faq__a--open')}
      >
        <Text>{item.description}</Text>
      </div>
    </>
  )
}

export function Faq(p: SectionComponentProps<'faq'>) {
  const { content, layout, id, theme, locale } = p

  const titleId = `${id}-title`
  const items = content.items ?? []

  return (
    <Section
      as="section"
      theme={theme}
      locale={locale}
      id={id}
      ariaLabelledBy={content.heading ? titleId : undefined}
      className={cx('sbp-faq', `sbp-faq--${layout}`)}
    >
      <Container className="sbp-faq__inner">
        <InfoBlock
          className="sbp-faq__info"
          eyebrowBar
          eyebrow={content.eyebrow}
          heading={content.heading}
          subheading={content.subheading}
          headingLevel={2}
          headingId={titleId}
          align="center"
        >
          <Actions actions={content.actions} align="center" size="lg" />
        </InfoBlock>

        {items.length > 0 ? (
          <ul className="sbp-faq__list">
            {items.map((item, index) => (
              <li key={item.id} className="sbp-faq__item">
                <FaqItem item={item} index={index} sectionId={id} />
              </li>
            ))}
          </ul>
        ) : null}
      </Container>
    </Section>
  )
}
